import Papa, { ParseResult } from "papaparse";
import * as XLSX from "xlsx";

export interface ParsedData {
  headers: string[];
  rows: any[];
  errors: string[];
}

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateFile = (file: File): FileValidationResult => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    "text/csv",
    "application/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel", // .xls
    "application/json",
  ];

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: "File size must be less than 10MB",
    };
  }

  // Check file extension as fallback for MIME type issues
  const fileName = file.name.toLowerCase();
  const hasValidExtension = 
    fileName.endsWith('.csv') || 
    fileName.endsWith('.xlsx') || 
    fileName.endsWith('.xls') || 
    fileName.endsWith('.json');

  if (!allowedTypes.includes(file.type) && !hasValidExtension) {
    return {
      isValid: false,
      error: "Only CSV, Excel (.xlsx, .xls), and JSON files are allowed",
    };
  }

  return { isValid: true };
};

export const parseFile = async (file: File): Promise<ParsedData> => {
  const fileName = file.name.toLowerCase();
  const errors: string[] = [];

  try {
    if (fileName.endsWith('.json')) {
      return await parseJSONFile(file);
    } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      return await parseExcelFile(file);
    } else {
      return await parseCSVFile(file);
    }
  } catch (error) {
    return {
      headers: [],
      rows: [],
      errors: [error instanceof Error ? error.message : "Failed to parse file"],
    };
  }
};

const parseJSONFile = async (file: File): Promise<ParsedData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);
        
        let rows: any[] = [];
        let headers: string[] = [];

        // Handle different JSON structures
        if (Array.isArray(data)) {
          // Array of objects
          if (data.length > 0) {
            headers = Object.keys(data[0]);
            rows = data;
          }
        } else if (typeof data === 'object' && data !== null) {
          // Object with data property or direct object
          if (data.data && Array.isArray(data.data)) {
            if (data.data.length > 0) {
              headers = Object.keys(data.data[0]);
              rows = data.data;
            }
          } else {
            // Single object
            headers = Object.keys(data);
            rows = [data];
          }
        }

        resolve({
          headers,
          rows,
          errors: [],
        });
      } catch (error) {
        reject(new Error("Invalid JSON format"));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
};

const parseExcelFile = async (file: File): Promise<ParsedData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON with headers
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length === 0) {
          resolve({
            headers: [],
            rows: [],
            errors: ["Excel file is empty"],
          });
          return;
        }

        const headers = (jsonData[0] as string[]).map(h => h?.toString() || '');
        const rows = jsonData.slice(1).map((row: any) => {
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] || null;
          });
          return obj;
        });

        resolve({
          headers,
          rows,
          errors: [],
        });
      } catch (error) {
        reject(new Error("Failed to parse Excel file"));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
};

const parseCSVFile = async (file: File): Promise<ParsedData> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<any>) => {
        if (results.errors && results.errors.length > 0) {
          resolve({
            headers: results.meta?.fields || [],
            rows: results.data || [],
            errors: results.errors.map(e => e.message || "CSV parsing error"),
          });
        } else {
          resolve({
            headers: results.meta?.fields || [],
            rows: results.data || [],
            errors: [],
          });
        }
      },
      error: (error) => {
        reject(new Error(error.message || "Failed to parse CSV"));
      },
    });
  });
};

export const sanitizeValue = (value: any): any => {
  if (typeof value === "string") {
    // Prevent CSV injection
    if (value.startsWith("=")) {
      return `'${value}`;
    }
    // Trim whitespace
    return value.trim();
  }
  return value;
};

export const getFileType = (file: File): string => {
  const fileName = file.name.toLowerCase();
  if (fileName.endsWith('.json')) return 'JSON';
  if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) return 'Excel';
  return 'CSV';
}; 